import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <cos-skeleton> — loading placeholder block with a subtle shimmer.
 *
 * Stage 19c added the `variant` + `count` API for richer loading
 * shapes. Backwards-compatible with the original
 * `<cos-skeleton width="120px" height="16px">` form — that still
 * renders a single text-line skeleton.
 *
 * Variants:
 *   text — 1em-tall block, full width by default. Default variant.
 *   row  — 48px-tall block with a bottom border. Stack N of these
 *          inside a list to mimic table rows.
 *   card — 120px-tall block with rounded corners + border. Stack N
 *          to mimic a list of cards.
 *
 * Use `count` to repeat the block; spacing follows the variant's
 * natural rhythm (rows have border-bottom, cards have margin).
 *
 * Loading skeletons must NEVER share a render path with error UI —
 * the AsyncState type makes this impossible at the type level, but
 * if a component bypasses the controller it's still on the author
 * to keep the two states mutually exclusive.
 */
@customElement("cos-skeleton")
export class CosSkeleton extends LitElement {
	@property({ type: String }) variant: "text" | "row" | "card" = "text";
	@property({ type: Number }) count = 1;
	@property({ type: String }) width = "100%";
	@property({ type: String }) height = "";

	static styles = css`
		:host {
			display: block;
		}
		:host([variant="text"]) {
			display: inline-block;
		}
		.s {
			display: block;
			position: relative;
			overflow: hidden;
			border-radius: 4px;
			background: var(--cos-border);
		}
		.s::after {
			content: "";
			position: absolute;
			inset: 0;
			transform: translateX(-100%);
			background: linear-gradient(
				90deg,
				transparent 0%,
				color-mix(in srgb, var(--cos-surface) 55%, transparent) 50%,
				transparent 100%
			);
			animation: shimmer 1.4s linear infinite;
		}
		.row-wrap {
			display: flex;
			align-items: center;
			padding: var(--cos-space-3) 0;
			border-bottom: 1px solid var(--cos-surface-raised);
		}
		.row-wrap:last-child { border-bottom: none; }
		.row-wrap .s {
			height: 14px;
			width: 100%;
		}
		.card-wrap {
			padding: var(--cos-space-4);
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-card);
			background: var(--cos-surface);
			margin-bottom: var(--cos-space-3);
		}
		.card-wrap:last-child { margin-bottom: 0; }
		.card-wrap .s {
			height: 14px;
			margin-bottom: var(--cos-space-2);
		}
		.card-wrap .s:last-child { margin-bottom: 0; }
		.card-wrap .s.short  { width: 40%; }
		.card-wrap .s.medium { width: 60%; }
		.card-wrap .s.long   { width: 90%; }

		@keyframes shimmer {
			to { transform: translateX(100%); }
		}
		@media (prefers-reduced-motion: reduce) {
			.s::after { animation: none; display: none; }
		}
	`;

	render() {
		const n = Math.max(1, this.count);
		switch (this.variant) {
			case "row":
				return html`${repeat(n, () => html`
					<div class="row-wrap" role="presentation" aria-hidden="true">
						<span class="s"></span>
					</div>
				`)}`;
			case "card":
				return html`${repeat(n, () => html`
					<div class="card-wrap" role="presentation" aria-hidden="true">
						<span class="s short"></span>
						<span class="s long"></span>
						<span class="s medium"></span>
					</div>
				`)}`;
			case "text":
			default: {
				// Backwards-compat: a single inline-block sized by the
				// classic width/height props. height defaults to 14px
				// when not set explicitly (preserves Stage 0 callers).
				const h = this.height || "14px";
				return html`${repeat(n, () => html`
					<span class="s" role="presentation" aria-hidden="true"
						style=${`width:${this.width};height:${h};`}></span>
				`)}${this.count > 1 ? nothing : nothing}`;
			}
		}
	}
}

// Local repeat helper: avoids importing lit/directives/repeat for this
// trivial case (count is a small fixed number, no key churn).
function repeat<T>(n: number, fn: (i: number) => T): T[] {
	const out: T[] = [];
	for (let i = 0; i < n; i++) out.push(fn(i));
	return out;
}

declare global {
	interface HTMLElementTagNameMap {
		"cos-skeleton": CosSkeleton;
	}
}
